import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

const CardTemplate1 = ({ cardData = {} }) => {
  const {
    name = 'John Doe',
    title = 'Software Engineer',
    company = 'Tech Solutions Inc.',
    email = 'john.doe@example.com',
    phone = '+1 (555) 123-4567',
    location = 'New York, NY',
    bio = 'Passionate software engineer with 5+ years of experience in web development and cloud technologies.',
    linkedin = 'https://linkedin.com/in/johndoe',
    twitter = 'https://twitter.com/johndoe',
    instagram = 'https://instagram.com/johndoe',
    facebook = 'https://facebook.com/johndoe',
    website = 'https://johndoe.com',
    profileImage = null,
  } = cardData;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${name} - Digital Business Card`,
        text: `Connect with ${name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleDownload = () => {
    // In a real implementation, this would generate a vCard or PDF
    alert('Download functionality would be implemented here');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          borderRadius: 1,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 4,
            textAlign: 'center',
            color: 'white',
          }}
        >
          <Avatar
            src={profileImage}
            sx={{
              width: 120,
              height: 120,
              mx: 'auto',
              mb: 2,
              border: '4px solid white',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
            }}
          >
            {name.charAt(0)}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {name}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            {company}
          </Typography>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 3 }}>
          {/* Bio */}
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
            {bio}
          </Typography>

          {/* Contact Information */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ color: 'primary.main', mr: 2 }} />
              <Typography variant="body2">{email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneIcon sx={{ color: 'primary.main', mr: 2 }} />
              <Typography variant="body2">{phone}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ color: 'primary.main', mr: 2 }} />
              <Typography variant="body2">{location}</Typography>
            </Box>
            {website && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'primary.main', mr: 2 }}>🌐</Typography>
                <Typography variant="body2">{website}</Typography>
              </Box>
            )}
          </Box>

          {/* Social Links */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Connect with me
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {linkedin && (
                <Chip
                  icon={<LinkedInIcon />}
                  label="LinkedIn"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              )}
              {twitter && (
                <Chip
                  icon={<TwitterIcon />}
                  label="Twitter"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              )}
              {instagram && (
                <Chip
                  icon={<InstagramIcon />}
                  label="Instagram"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              )}
              {facebook && (
                <Chip
                  icon={<FacebookIcon />}
                  label="Facebook"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleShare}
              sx={{
                borderRadius: 1,
                px: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
              }}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ borderRadius: 1, px: 3 }}
            >
              Save
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CardTemplate1;



